import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, mediaUrl } from "../lib/api";
import Reveal from "../components/Reveal";

export default function TalentsPage() {
    const [items, setItems] = useState([]);
    const [filter, setFilter] = useState("Tutti");

    useEffect(() => {
        api.get("/talents").then((r) => setItems(r.data)).catch(() => setItems([]));
    }, []);

    const categories = useMemo(() => {
        const set = new Set(items.map((i) => i.category).filter(Boolean));
        return ["Tutti", ...Array.from(set)];
    }, [items]);

    const filtered = filter === "Tutti" ? items : items.filter((i) => i.category === filter);

    return (
        <div data-testid="talents-page">
            <section className="relative pt-36 md:pt-44 pb-16 md:pb-24 overflow-hidden">
                <img
                    src="https://images.unsplash.com/photo-1772758058699-5b3f1abc384d?crop=entropy&cs=srgb&fm=jpg&q=80&w=2000"
                    alt=""
                    className="absolute inset-0 w-full h-full object-cover opacity-30"
                />
                <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black" />
                <div className="container-nm relative">
                    <Reveal>
                        <div className="kicker mb-6">Talent & Ospiti</div>
                    </Reveal>
                    <Reveal delay={100}>
                        <h1 className="font-display text-[2.5rem] sm:text-5xl md:text-6xl lg:text-[5.5rem] font-extrabold leading-[0.95] tracking-[-0.035em]">
                            I volti che hanno lavorato con noi
                        </h1>
                    </Reveal>
                    <Reveal delay={200}>
                        <p className="mt-8 text-lg md:text-xl text-white/75 max-w-3xl leading-relaxed">
                            Nel corso delle nostre produzioni televisive ed eventi abbiamo collaborato con
                            talent e personaggi di rilievo del panorama nazionale.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section className="border-y border-white/10" data-testid="talent-filters">
                <div className="container-nm flex flex-wrap gap-2 md:gap-4 py-6 overflow-x-auto">
                    {categories.map((c) => (
                        <button
                            key={c}
                            onClick={() => setFilter(c)}
                            data-testid={`filter-${c.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`px-5 py-2 text-xs uppercase tracking-[0.2em] border transition-all duration-300 ${
                                filter === c
                                    ? "bg-[color:var(--brand-blue)] text-white border-[color:var(--brand-blue)]"
                                    : "border-white/20 text-white/70 hover:text-white hover:border-white"
                            }`}
                        >
                            {c}
                        </button>
                    ))}
                </div>
            </section>

            <section className="container-nm py-16 md:py-20">
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                    {filtered.map((t, i) => (
                        <Reveal delay={(i % 8) * 60} key={t.id}>
                            <Link
                                to={`/talent-ospiti/${t.slug}`}
                                className="editorial-card group block"
                                data-testid={`talent-card-${t.slug}`}
                            >
                                <div className="img-wrap aspect-[3/4] bg-neutral-900">
                                    {t.cover_image && (
                                        <img
                                            src={mediaUrl(t.cover_image)}
                                            alt={t.title}
                                            loading="lazy"
                                            className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700"
                                        />
                                    )}
                                </div>
                                <div className="mt-4">
                                    <div className="font-display text-lg md:text-xl font-bold group-hover:text-[color:var(--brand-blue)] transition-colors">
                                        {t.title}
                                    </div>
                                    <div className="kicker text-white/40 mt-1">{t.category}</div>
                                </div>
                            </Link>
                        </Reveal>
                    ))}
                </div>
            </section>

            <section className="bg-[#F9F8F6] text-black py-24" data-testid="talents-strategy">
                <div className="container-nm grid grid-cols-1 md:grid-cols-12 gap-10">
                    <Reveal className="md:col-span-7">
                        <div className="kicker mb-5">Approccio</div>
                        <h2 className="font-display text-4xl md:text-5xl font-extrabold tracking-[-0.03em] leading-[0.95]">
                            Visibilità e coinvolgimento costruiti su misura
                        </h2>
                    </Reveal>
                    <Reveal delay={120} className="md:col-span-5 flex items-end">
                        <p className="text-lg text-neutral-700 leading-relaxed">
                            Le collaborazioni con talent e personaggi pubblici sono parte integrante del
                            nostro approccio: ogni progetto è costruito per generare visibilità e
                            coinvolgimento.
                        </p>
                    </Reveal>
                </div>
                <div className="container-nm mt-14 text-xs uppercase tracking-[0.2em] text-neutral-500">
                    Nota legale: alcuni contenuti potrebbero essere soggetti a diritti di immagine.
                </div>
            </section>

            <section className="bg-[color:var(--brand-blue)] text-white py-24" data-testid="talents-cta">
                <div className="container-nm grid grid-cols-1 md:grid-cols-12 gap-10 items-end">
                    <div className="md:col-span-7">
                        <h3 className="font-display text-4xl md:text-6xl font-extrabold tracking-[-0.03em] leading-[0.95]">
                            Vuoi collaborare con noi?
                        </h3>
                    </div>
                    <div className="md:col-span-5 flex flex-wrap gap-4">
                        <Link to="/contatti" className="btn-ghost text-white">Entra in contatto</Link>
                        <Link to="/contatti?kind=partner" className="btn-ghost text-white">
                            Proponi una collaborazione
                        </Link>
                    </div>
                </div>
            </section>
        </div>
    );
}
